<template>
    <div class="card">
        <div class="card-header">
            <h4 class="card-title">
                {{ titleText }}
            </h4>
        </div>
        <form class="card-body me-3 ms-3" @submit.prevent="handleSubmit">
            <div v-for="(inputGroup, index) in filteredFormConfig" :key="index">
                <!-- 單一物件的情況 -->
                <div v-if="!Array.isArray(inputGroup)" class="form-group-row">
                    <UserFormGroupInput
                        ref="inputRefs"
                        v-bind="inputGroup"
                        :key="
                            inputGroup.field === 'stations'
                                ? `stations-${componentKey}`
                                : inputGroup.field === 'managed_stations'
                                ? `managed-stations-${componentKey}`
                                : inputGroup.field
                        "
                        :add-option-params="getAddOptionParams(inputGroup)"
                        v-model="formData[inputGroup.field]"
                        @valid="handleValidation"
                    />
                </div>
                <!-- 多個物件（數組）的情況 -->
                <div v-else class="form-group-row d-flex">
                    <div
                        v-for="(inputConfig, subIndex) in inputGroup"
                        :key="subIndex"
                        class="flex-fill"
                    >
                        <UserFormGroupInput
                            ref="inputRefs"
                            v-bind="inputConfig"
                            :key="
                                inputConfig.field === 'stations'
                                    ? `stations-${componentKey}`
                                    : inputConfig.field === 'managed_stations'
                                    ? `managed-stations-${componentKey}`
                                    : inputConfig.field
                            "
                            :add-option-params="getAddOptionParams(inputConfig)"
                            v-model="formData[inputConfig.field]"
                            @valid="handleValidation"
                        />
                    </div>
                </div>
            </div>
            <div class="d-grid gap-2 d-md-flex justify-content-md-start">
                <button
                    type="submit"
                    class="btn btn-primary"
                    :disabled="submitDisabled"
                >
                    {{ $t('users.users_msg__user_form_submit') }}
                </button>
                <button
                    type="reset"
                    class="btn btn-secondary"
                    @click="handleReset"
                >
                    {{ $t('users.users_msg__create_account_reset') }}
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import { ref, inject, computed, watch } from 'vue';
import { UserFormGroupInput } from '@/components';
import { useI18n } from 'vue-i18n';
import { fetchCreateUserConfig, createUser } from '@/api/users';
import { transformFormPayload } from '@/utils/formPayload';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

export default {
    name: 'UserCreate',
    components: {
        UserFormGroupInput,
    },
    props: {
        userAccountLevel: {
            type: String,
            default: 'Account',
            required: false,
        },
    },
    setup(props) {
        const formConfig = ref([]);
        const formData = ref({});
        const submitDisabled = ref(true);
        const componentKey = ref(0); // 用於強制重新渲染
        const { t } = useI18n();
        const router = useRouter();
        const Swal = inject('$swal');
        const inputRefs = ref([]);
        const store = useStore();

        const userSelf = computed(() => store.state.auth.userSelf);

        const stationsFieldName = ref('stations');
        const managedStationsFieldName = ref('managed_stations');

        const titleText = computed(() => {
            if (props.userAccountLevel === 'Employee') {
                return t('sidebar.sidebar_msg__create_a_employee_user');
            } else if (props.userAccountLevel === 'Business') {
                return t('sidebar.sidebar_msg__create_a_business_user');
            } else {
                return t('sidebar.sidebar_msg__create_a_user');
            }
        });

        const activeAccountLevel = computed(
            () => formData.value.account_level || props.userAccountLevel
        );

        const filteredFormConfig = computed(() => {
            const allow = cfg =>
                !cfg.visible_when ||
                cfg.visible_when.includes(activeAccountLevel.value);

            return formConfig.value
                .map(item => (Array.isArray(item) ? item.filter(allow) : item))
                .filter(item =>
                    Array.isArray(item) ? item.length : allow(item)
                );
        });

        const initialize = async () => {
            try {
                formConfig.value = await fetchCreateUserConfig(
                    props.userAccountLevel
                );

                // club means clubs select field, club_name means readonly field
                const clubField = formConfig.value.find(
                    config =>
                        config.field === 'club' || config.field === 'club_name'
                );
                if (clubField && clubField.type === 'readonly') {
                    // 直接從 store 中獲取 club_name
                    formData.value.club = userSelf.value.club || '';
                    formData.value.club_name = userSelf.value.club_name || '';
                    // 設定 payload_field，讓 transformFormPayload 知道要使用 formData.club 的值
                    clubField.payload_field = 'club';
                } else if (clubField && clubField.type === 'select') {
                    // 不需要手動獲取 clubs，讓 UserFormGroupInput 自己處理
                    // clubField 已經有 options_url，組件會自動載入
                }

                const initializeStationsField = fieldName => {
                    const stationField = formConfig.value.find(
                        config => config && config.field === fieldName
                    );
                    if (stationField) {
                        stationField.options_url =
                            '/users/stations?search_fields=club__id&search=0&search_mode=exact';
                        formData.value[fieldName] = [];
                    }
                };

                initializeStationsField(stationsFieldName.value);
                initializeStationsField(managedStationsFieldName.value);

                // 初始化表單數據
                formConfig.value.forEach(config => {
                    if (Array.isArray(config)) {
                        config.forEach(subConfig => {
                            // 確保 stations / managed_stations 初始值是空數組而不是空字符串
                            if (
                                subConfig.field === 'stations' ||
                                subConfig.field === 'managed_stations'
                            ) {
                                formData.value[subConfig.field] = [];
                            } else {
                                formData.value[subConfig.field] =
                                    subConfig.value || '';
                            }
                        });
                    } else {
                        // 確保 stations / managed_stations 初始值是空數組而不是空字符串
                        if (
                            config.field === 'stations' ||
                            config.field === 'managed_stations'
                        ) {
                            formData.value[config.field] = [];
                        } else {
                            formData.value[config.field] = config.value || '';
                        }
                    }
                });
            } catch (error) {
                console.error('Error fetching form configuration:', error);
            }
        };

        // 等 userSelf 有值才初始化表單
        watch(
            () => userSelf.value,
            val => {
                if (val) initialize();
            },
            { immediate: true }
        );

        watch(
            () => props.userAccountLevel,
            async () => {
                if (userSelf.value) initialize();
            }
        );

        const updateStationsOptionsByClubId = clubId => {
            const updateField = fieldName => {
                const stationField = formConfig.value.find(
                    config => config && config.field === fieldName
                );
                if (
                    stationField &&
                    (stationField.type === 'select' ||
                        stationField.type === 'multi-select')
                ) {
                    if (clubId && clubId !== 0) {
                        const newUrl = `/users/stations?search_fields=club__id&search=${clubId}&search_mode=exact`;
                        stationField.options_url = newUrl;
                    } else {
                        stationField.options_url =
                            '/users/stations?search_fields=club__id&search=0&search_mode=exact';
                    }
                }
            };

            updateField(stationsFieldName.value);
            updateField(managedStationsFieldName.value);
        };

        // 當 club 變更時，透過修改 options_url 來觸發 stations 的重新載入
        watch(() => formData.value.club, updateStationsOptionsByClubId);

        const updateStationsOptionsByClubName = clubName => {
            const updateField = fieldName => {
                const stationField = formConfig.value.find(
                    config => config && config.field === fieldName
                );
                if (
                    stationField &&
                    (stationField.type === 'select' ||
                        stationField.type === 'multi-select')
                ) {
                    if (clubName && clubName.trim() !== '') {
                        const newUrl = `/users/stations?search_fields=club__name&search=${encodeURIComponent(
                            clubName
                        )}`;
                        stationField.options_url = newUrl;
                    } else {
                        stationField.options_url =
                            '/users/stations?search_fields=club__id&search=0&search_mode=exact';
                    }
                }
            };

            updateField(stationsFieldName.value);
            updateField(managedStationsFieldName.value);
        };

        // 當 club_name 變更時，透過修改 options_url 來觸發 stations 的重新載入
        watch(() => formData.value.club_name, updateStationsOptionsByClubName);

        const handleValidation = isValid => {
            submitDisabled.value = !isValid;
        };

        const validateAll = () => {
            inputRefs.value.forEach(ref => {
                ref.checkValue();
            });
        };

        const handleSubmit = async () => {
            validateAll();

            const handleKeydown = event => {
                if (event.key === 'Enter') {
                    router.push('/users/list?sortColumn=cdate&sortOrder=desc');
                    Swal.close();
                } else if (event.key === 'Escape') {
                    location.reload();
                    Swal.close();
                }
            };

            if (submitDisabled.value) {
                return;
            }

            Swal.fire({
                title: 'Creating User',
                text: 'Please wait...',
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                },
            });

            // 使用 transformFormPayload 處理 payload 格式轉換
            const userData = transformFormPayload(
                formData.value,
                filteredFormConfig.value
            );

            Swal.close();
            try {
                await createUser(userData);
                await Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: t('users.users_msg__create_a_new_user_successful', {
                        username: userData.username,
                    }),
                    showConfirmButton: true,
                    showDenyButton: true,
                    confirmButtonText: t(
                        'users.users_msg__create_another_user'
                    ),
                    denyButtonText: t('users.users_msg__go_to_user_list'),
                    stopKeydownPropagation: false, // 允许键盘事件传播
                    didOpen: () => {
                        window.addEventListener('keydown', handleKeydown);
                    },
                    willClose: () => {
                        window.removeEventListener('keydown', handleKeydown);
                    },
                    didClose: () => {
                        location.reload();
                    },
                }).then(result => {
                    if (result.isConfirmed) {
                        // reload the window
                        location.reload();
                    } else if (result.isDenied) {
                        router.push(
                            '/users/list?sortColumn=cdate&sortOrder=desc'
                        );
                    }
                });
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: t(`users.${error.response.data.detail}`),
                });
            }
        };

        const handleReset = () => {
            formConfig.value.forEach(config => {
                if (Array.isArray(config)) {
                    config.forEach(subConfig => {
                        formData.value[subConfig.field] = subConfig.value || '';
                    });
                } else {
                    formData.value[config.field] = config.value || '';
                }
            });
        };

        const getAddOptionParams = inputConfig => {
            // 如果是 stations / managed_stations 欄位，加入 club 參數
            if (
                inputConfig.field === 'stations' ||
                inputConfig.field === 'managed_stations'
            ) {
                // 獲取 club ID，優先使用 club，如果沒有則嘗試從 userSelf 獲取
                let clubId = formData.value.club;

                // 如果 formData.value.club 沒有值，但有 club_name（readonly 模式）
                if (!clubId && formData.value.club_name) {
                    clubId = userSelf.value?.club;
                }

                if (!clubId) {
                    console.warn('No club ID available for adding station');
                    // 可以選擇顯示警告或返回空對象
                    // Swal.fire({
                    //     icon: 'warning',
                    //     title: 'Warning',
                    //     text: t('users.users_msg__please_select_club_first'),
                    // });
                    return {};
                }

                return {
                    club: clubId,
                };
            }
            // 其他欄位可以根據需要添加不同的參數
            return {};
        };

        return {
            titleText,
            formConfig,
            filteredFormConfig,
            formData,
            submitDisabled,
            componentKey,
            handleSubmit,
            handleValidation,
            handleReset,
            getAddOptionParams,
        };
    },
};
</script>

<style scoped>
.card {
    box-shadow: none;
}
.form-group-row {
    margin-bottom: 1rem;
    text-align: left;
}

.d-flex {
    display: flex;
    gap: 1rem;
}

.flex-fill {
    flex: 1;
}
</style>
